import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 px-6 py-12">
      <div className="max-w-3xl mx-auto space-y-8">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">
            Données personnelles
          </p>
          <h1 className="text-2xl sm:text-3xl font-semibold">Politique de confidentialité</h1>
          <p className="mt-2 text-sm text-slate-500">
            Dernière mise à jour : Avril 2026. Conformément au Règlement général sur la
            protection des données (RGPD — UE 2016/679) et à la loi Informatique et Libertés.
          </p>
        </header>

        {/* 1. Responsable */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-2 shadow-sm">
          <h2 className="text-lg font-semibold">1. Responsable du traitement</h2>
          <p className="text-sm text-slate-700">
            <span className="font-medium">William Jacob Bloch</span> — entrepreneur individuel
            exploitant sous la marque <span className="font-medium">Blackout Games</span>
          </p>
          <p className="text-sm text-slate-700">94210 Saint-Maur-des-Fossés, France</p>
          <p className="text-sm text-slate-700">
            SIREN : 942 523 762 —{" "}
            <a href="mailto:contact@blackout-games.fr" className="text-indigo-600 hover:text-indigo-500">
              contact@blackout-games.fr
            </a>
          </p>
        </section>

        {/* 2. Données collectées */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-3">2. Données collectées</h2>
          <div className="space-y-4 text-sm text-slate-700">
            <div>
              <p className="font-medium text-slate-800 mb-1">Compte entreprise</p>
              <p>Nom, adresse e-mail, et le cas échéant toute information communiquée lors de la création du compte ou via le formulaire de contact.</p>
            </div>
            <div>
              <p className="font-medium text-slate-800 mb-1">Sessions de jeu</p>
              <p>Codes session, statuts, dates de session, pseudonymes des joueurs, scores et résultats de partie. Ces données sont liées au compte entreprise acheteur.</p>
            </div>
            <div>
              <p className="font-medium text-slate-800 mb-1">Facturation</p>
              <p>Les données de paiement (numéro de carte, etc.) sont traitées exclusivement par <span className="font-medium">Stripe</span> et ne transitent pas par nos serveurs. Nous conservons uniquement l'identifiant de la transaction et le statut du paiement.</p>
            </div>
            <div>
              <p className="font-medium text-slate-800 mb-1">Données techniques</p>
              <p>Logs de connexion et d'erreur (adresse IP, horodatage, user-agent) conservés à des fins de sécurité et de débogage.</p>
            </div>
          </div>
        </section>

        {/* 3. Finalités et bases légales */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-3">3. Finalités et bases légales</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-700 border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="pb-2 pr-4 font-medium text-slate-500 text-xs uppercase tracking-wide">Finalité</th>
                  <th className="pb-2 font-medium text-slate-500 text-xs uppercase tracking-wide">Base légale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {[
                  ["Création et gestion du compte entreprise", "Exécution du contrat"],
                  ["Fourniture du service (sessions, codes, accès)", "Exécution du contrat"],
                  ["Facturation et archivage comptable", "Obligation légale"],
                  ["Envoi des e-mails transactionnels (confirmation, codes)", "Exécution du contrat"],
                  ["Amélioration du produit et résolution de bugs", "Intérêt légitime"],
                  ["Sécurité et prévention des fraudes", "Intérêt légitime"],
                ].map(([finalite, base]) => (
                  <tr key={finalite}>
                    <td className="py-2.5 pr-4">{finalite}</td>
                    <td className="py-2.5 text-slate-500">{base}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. Destinataires et sous-traitants */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-3">4. Destinataires et sous-traitants</h2>
          <div className="space-y-3 text-sm text-slate-700">
            {[
              { name: "Stripe", role: "Traitement des paiements", country: "États-Unis — clauses contractuelles types UE", url: "https://stripe.com/fr/privacy" },
              { name: "Brevo (ex-Sendinblue)", role: "Envoi des e-mails transactionnels", country: "France / UE", url: "https://www.brevo.com/fr/legal/privacypolicy/" },
              { name: "Render", role: "Hébergement du serveur (API, base de données)", country: "États-Unis — clauses contractuelles types UE", url: "https://render.com/privacy" },
              { name: "Netlify", role: "Hébergement du front-end", country: "États-Unis — clauses contractuelles types UE", url: "https://www.netlify.com/privacy/" },
            ].map(({ name, role, country, url }) => (
              <div key={name} className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
                <div>
                  <p><span className="font-medium">{name}</span> — {role}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {country} ·{" "}
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500">
                      Politique de confidentialité
                    </a>
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Aucune donnée n'est vendue à des tiers ni utilisée à des fins publicitaires.
          </p>
        </section>

        {/* 5. Durées de conservation */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-3">5. Durées de conservation</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-700 border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="pb-2 pr-4 font-medium text-slate-500 text-xs uppercase tracking-wide">Donnée</th>
                  <th className="pb-2 font-medium text-slate-500 text-xs uppercase tracking-wide">Durée</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {[
                  ["Compte entreprise et données associées", "Durée de la relation contractuelle + 3 ans"],
                  ["Données de facturation et pièces comptables", "10 ans (obligation légale)"],
                  ["Données de session de jeu (codes, scores, pseudos)", "3 ans après la dernière session"],
                  ["Logs techniques (IP, connexion, erreurs)", "12 mois glissants"],
                ].map(([donnee, duree]) => (
                  <tr key={donnee}>
                    <td className="py-2.5 pr-4">{donnee}</td>
                    <td className="py-2.5 text-slate-500">{duree}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 6. Droits des personnes */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3 shadow-sm">
          <h2 className="text-lg font-semibold">6. Vos droits</h2>
          <p className="text-sm text-slate-700">
            Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
            droit d'accès, de rectification, d'effacement, d'opposition, de limitation du traitement
            et de portabilité.
          </p>
          <p className="text-sm text-slate-700">
            Pour exercer ces droits, vous pouvez :
          </p>
          <ul className="text-sm text-slate-700 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              Envoyer un e-mail à{" "}
              <a href="mailto:contact@blackout-games.fr" className="text-indigo-600 hover:text-indigo-500 ml-1">
                contact@blackout-games.fr
              </a>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              Utiliser la fonctionnalité de suppression disponible dans{" "}
              <Link to="/profile" className="text-indigo-600 hover:text-indigo-500 ml-1">
                Mon espace → Mon compte → RGPD & données
              </Link>
            </li>
          </ul>
          <p className="text-sm text-slate-700">
            Nous nous engageons à répondre à toute demande dans un délai d'un mois.
          </p>
          <p className="text-sm text-slate-700">
            Si vous estimez que le traitement de vos données ne respecte pas la réglementation,
            vous pouvez introduire une réclamation auprès de la{" "}
            <a
              href="https://www.cnil.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-500"
            >
              Commission Nationale de l'Informatique et des Libertés (CNIL)
            </a>.
          </p>
        </section>

        {/* 7. Cookies */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-2 shadow-sm">
          <h2 className="text-lg font-semibold">7. Cookies et traceurs</h2>
          <p className="text-sm text-slate-700">
            Ce site n'utilise pas de cookies publicitaires ni d'outils de tracking tiers
            (pas de Google Analytics, pas de pixels de remarketing).
          </p>
          <p className="text-sm text-slate-700">
            Des cookies techniques strictement nécessaires au fonctionnement du service
            (authentification, session) peuvent être déposés. Ils ne requièrent pas de
            consentement au sens du RGPD.
          </p>
        </section>

        {/* 8. Modifications */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-2 shadow-sm">
          <h2 className="text-lg font-semibold">8. Modifications</h2>
          <p className="text-sm text-slate-700">
            Cette politique peut être mise à jour à tout moment. La date de dernière modification
            est indiquée en haut de page. En cas de modification substantielle, les utilisateurs
            disposant d'un compte seront informés par e-mail.
          </p>
        </section>
      </div>
    </div>
  );
}